import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import BackBar from '../components/BackBar';
import api from '../../api/api';
import LocationPicker from '../components/LocationPicker';
import VerifyIdentityModal from '../components/VerifyIdentityModal';
import { createSubmissionLock, emptyLocation, locationFromText, normalizeListingError, runOnce, validateClassifiedListing, validateImageSelection } from '../utils/listingForm';

// Categories fetched from GET /categories (src/categories/categories.data.ts
// — the single source of truth). This used to be its own independently-
// hardcoded 15-category list that had drifted from the canonical one (no
// pets/construction/industrial, different subcategory keys/specs) — one of
// three divergent category lists in the app. This fallback only covers the
// brief window before that fetch resolves.
const DEFAULT_MEDIA_RULES = { minImages: 2, recommendedImages: 6, maxImages: 10, guidanceText: 'Clear photos from multiple angles help buyers trust your listing.' };
const FALLBACK_CATEGORIES = {
  general: { label: 'General', icon: '📦', mediaRules: DEFAULT_MEDIA_RULES, subcategories: { other: { label: 'Other', attributes: [
    { key: 'brand', label: 'Brand', type: 'text' }, { key: 'model', label: 'Model', type: 'text' },
    { key: 'condition', label: 'Condition', type: 'select', allowedValues: ['New', 'Used', 'Refurbished'] },
    { key: 'color', label: 'Color', type: 'multiselect' },
  ] } } },
};


const labelStyle = { display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: '600' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '16px', boxSizing: 'border-box', outline: 'none' };

const EMPTY_FORM = {
  title: '', description: '', price: '',
  category: 'general', subcategory: 'other', location: '',
  images: [], specs: {}, condition: '', isNegotiable: false,
  isFlashSale: false, flashSalePrice: '', flashSaleEndsAt: '', flashSaleQuantity: '',
  contactPhone: '',
};

const SellerClassifieds = ({ onNavigate, isLoggedIn, onLogout, userRole, currentUser, editItemId, activeProfileId, createOnly = false }) => {
  const { t } = useTranslation();
  const [CATEGORIES, setCategories]   = useState(FALLBACK_CATEGORIES);
  const [classifieds, setClassifieds] = useState([]);
  const [classifiedLocation, setClassifiedLocation] = React.useState(emptyLocation);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [message, setMessage]         = useState('');
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  const [loadingPrice,  setLoadingPrice]     = useState(false);
  const [showForm, setShowForm]       = useState(createOnly);
  const [editItem, setEditItem]       = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const submitLock = useRef(createSubmissionLock());
  const [imagePreviews, setImagePreviews] = useState([]);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [userPhone, setUserPhone]     = useState('');
  const [descGenerating, setDescGenerating] = useState(false);
  const [showVerifyIdentity, setShowVerifyIdentity] = useState(false);
  // Same auto-suggest pattern as SellerProducts.js — see AiCategorySuggestionService.
  const [categoryManuallySet, setCategoryManuallySet] = useState(false);
  const [categorySuggested, setCategorySuggested]     = useState(false);
  const [suggestingCategory, setSuggestingCategory]   = useState(false);

  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  // Derived
  const currentCat  = CATEGORIES[form.category] || CATEGORIES.general;
  const subOptions  = Object.entries(currentCat.subcategories);
  const currentSub  = currentCat.subcategories[form.subcategory];
  const attrFields  = currentSub?.attributes || [];
  const mediaRules  = currentCat.mediaRules || DEFAULT_MEDIA_RULES;

  // Same fetch pattern SellerProducts.js already uses against the
  // canonical GET /categories — replaces the independently-hardcoded list
  // this form used to carry.
  useEffect(() => {
    api.get('/categories').then(res => {
      const tree = {};
      (res.data || []).forEach(cat => {
        const subcategories = {};
        (cat.subcategories || []).forEach(sub => {
          subcategories[sub.key] = { label: sub.label, attributes: sub.attributes || [] };
        });
        tree[cat.key] = { label: cat.label, icon: cat.icon, mediaRules: cat.mediaRules || DEFAULT_MEDIA_RULES, subcategories };
      });
      if (Object.keys(tree).length) setCategories(tree);
    }).catch(() => {});
  }, []);

  // Pre-fill location from seller's stored businessLocation
  React.useEffect(() => {
    if (!currentUser?.businessLocation && !currentUser?.businessDistrict) return;
    setForm(prev => ({
      ...prev,
      location: prev.location || currentUser.businessLocation || 
                [currentUser.businessDistrict, currentUser.businessRegion].filter(Boolean).join(', ') || '',
    }));
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchPriceSuggestion = useCallback(async (category, title, condition) => {
    if (!category) return;
    try {
      setLoadingPrice(true);
      const params = new URLSearchParams({ category });
      if (title) params.set('title', title);
      if (condition) params.set('condition', condition);
      const res = await api.get(`/classifieds/price-suggestion?${params}`);
      setPriceSuggestion(res.data);
    } catch { setPriceSuggestion(null); }
    finally { setLoadingPrice(false); }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) { onNavigate('PublicLogin'); return; }
    // createOnly is an immediate-create destination, not a management
    // dashboard — skip loading the existing-listings list entirely.
    if (!createOnly) fetchMyClassifieds();
    // Check if user has a phone number — required for classified contact
    api.get('/auth/profile').then(res => setUserPhone(res.data?.phone || '')).catch(() => {});
  // Re-fetch when the active profile changes, same reasoning as SellerProducts.js.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  // Deep-linked straight into editing one listing (e.g. from its Classified
  // Detail page's ••• menu) — open the edit form the moment it's loaded.
  useEffect(() => {
    if (!editItemId || !classifieds.length) return;
    const match = classifieds.find(c => c.id === Number(editItemId));
    if (match) handleEdit(match);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifieds, editItemId]);

  const handleCategoryChange = (cat, opts = {}) => {
    setShowOptionalDetails(false);
    const firstSub = Object.keys(CATEGORIES[cat]?.subcategories || {})[0] || '';
    setForm(prev => ({ ...prev, category: cat, subcategory: firstSub, specs: {}, condition: '' }));
    if (!opts.fromSuggestion) { setCategoryManuallySet(true); setCategorySuggested(false); }
  };

  const handleTitleBlur = async () => {
    if (categoryManuallySet || !form.title.trim() || form.title.trim().length < 3) return;
    try {
      setSuggestingCategory(true);
      const res = await api.post('/classifieds/ai/suggest-category', { title: form.title.trim() });
      if (res.data?.category && CATEGORIES[res.data.category]) {
        handleCategoryChange(res.data.category, { fromSuggestion: true });
        if (res.data.subcategory && CATEGORIES[res.data.category].subcategories[res.data.subcategory]) {
          setForm(prev => ({ ...prev, subcategory: res.data.subcategory }));
        }
        setCategorySuggested(true);
      }
    } catch { /* silent — a suggestion failure must never block posting */ }
    finally { setSuggestingCategory(false); }
  };

  const handleSubcategoryChange = (sub) => {
    setShowOptionalDetails(false);
    setForm(prev => ({ ...prev, subcategory: sub, specs: {}, condition: '' }));
  };

  const handleSpecChange = (key, value) => {
    setForm(prev => ({ ...prev, specs: { ...prev.specs, [key]: value } }));
  };

  // Same comma-joined-string convention as SellerProducts.js's multiselect
  // handling — stays within the existing Record<string,string> specs shape.
  const toggleMultiSpec = (key, option) => {
    setForm(prev => {
      const current = (prev.specs?.[key] || '').split(',').map(v => v.trim()).filter(Boolean);
      const next = current.includes(option) ? current.filter(v => v !== option) : [...current, option];
      return { ...prev, specs: { ...prev.specs, [key]: next.join(',') } };
    });
  };

  const fetchMyClassifieds = async () => {
    try {
      setLoading(true);
      // Scoped to the active profile — see SellerProducts.js's identical fix
      // (profile-architecture-audit-2026-08).
      const res = await api.get('/classifieds/user/mine', {
        params: activeProfileId ? { commerceProfileId: activeProfileId } : {},
      });
      setClassifieds(res.data);
    } catch { setError(t('seller_classifieds.load_failed')); }
    finally { setLoading(false); }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const selectionError = validateImageSelection(files, mediaRules.maxImages - form.images.length);
    if (selectionError) {
      setError(t(`listing_validation.${selectionError}`, { count: mediaRules.maxImages }));
      e.target.value = '';
      return;
    }
    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const res = await api.post('/upload/images', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const urls = res.data?.urls || [];
      if (urls.length !== files.length) throw new Error('Incomplete image upload');
      setForm(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      setImagePreviews(prev => [...prev, ...urls]);
    } catch (err) { setError(normalizeListingError(err, t('seller_classifieds.image_upload_failed'))); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removeImage = (i) => {
    setImagePreviews(prev => prev.filter((_, idx) => idx !== i));
    setForm(prev => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }));
  };

  // Reads the already-uploaded photo(s) + typed title and writes a
  // grounded description — never invents specs the photo/title don't
  // support. Fills the textarea for review; never submits on its own.
  const generateDescription = async () => {
    if (!form.title || !form.images.length) return;
    try {
      setDescGenerating(true);
      setError('');
      const res = await api.post('/classifieds/ai/generate-description', {
        title: form.title,
        imageUrls: form.images,
        categoryHint: currentCat?.label,
      });
      setForm(prev => ({ ...prev, description: res.data?.description || prev.description }));
    } catch { setError(t('seller_classifieds.ai_description_failed')); }
    finally { setDescGenerating(false); }
  };

  const resetForm = () => {
    setShowForm(false); setEditItem(null); setError('');
    setImagePreviews([]); setForm(EMPTY_FORM); setClassifiedLocation(emptyLocation());
    setCategoryManuallySet(false); setCategorySuggested(false);
  };

  const handleSubmit = async () => {
    if (submitLock.current.current) return;
    const validation = validateClassifiedListing({
      form,
      imageCount: form.images.length,
      minImages: mediaRules.minImages,
      maxImages: mediaRules.maxImages,
      requiredAttributes: attrFields.filter(a => a.required),
    });
    if (validation) {
      const suffix = validation.fields?.length ? `: ${validation.fields.join(', ')}` : '';
      setError(`${t(`listing_validation.${validation.code}`, { count: validation.count })}${suffix}`);
      return;
    }
    try {
      setSaving(true);
      setError('');
      let createdId = null;
      await runOnce(submitLock.current, async () => {
      const payload = {
        ...form,
        price:      Number(form.price),
        isFlashSale: form.isFlashSale,
        flashSalePrice: form.isFlashSale ? Number(form.flashSalePrice) : null,
        flashSaleEndsAt: form.isFlashSale ? new Date(form.flashSaleEndsAt).toISOString() : null,
        flashSaleQuantity: form.isFlashSale ? Number(form.flashSaleQuantity) : null,
        specs:      Object.keys(form.specs || {}).length > 0 ? form.specs : null,
        subcategory: form.subcategory || null,
        condition: attrFields.some(a => a.key === 'condition') ? (form.specs?.condition || null) : null,
        contactPhone: form.contactPhone || undefined,
      };
      if (editItem) {
        await api.patch(`/classifieds/${editItem.id}`, payload);
        setMessage(t('seller_classifieds.listing_updated'));
      } else {
        // Acting identity is resolved by the server from RoleContext. A
        // personal classified stays account-scoped; an organizational one
        // is stamped from its authoritative workspace.
        const created = await api.post('/classifieds', payload);
        createdId = created.data?.id ?? null;
        setMessage(t('seller_classifieds.listing_posted'));
      }
      });
      resetForm();
      if (createOnly && createdId != null) {
        onNavigate(`ClassifiedDetail-${createdId}`);
        return;
      }
      fetchMyClassifieds();
    } catch (err) {
      // Posting requires Level 1 identity verification — show the inline
      // "Verify Your Identity" flow instead of a plain error so the
      // already-filled form is never lost or restarted (spec section 5).
      if (err?.response?.data?.code === 'VERIFICATION_REQUIRED') {
        setShowVerifyIdentity(true);
        return;
      }
      setError(normalizeListingError(err, t('seller_classifieds.save_failed')));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setCategoryManuallySet(true); // editing a real listing — never auto-suggest over its actual category
    // Same migration as SellerProducts.js's handleEdit — old listings stored
    // specs keyed by raw label text ("Make") rather than the new
    // AttributeDef.key ("make"); match by label so old values still show.
    const targetAttrs = CATEGORIES[item.category]?.subcategories?.[item.subcategory]?.attributes || [];
    const migratedSpecs = { ...(item.specs || {}) };
    targetAttrs.forEach(attr => {
      if (migratedSpecs[attr.key] === undefined) {
        const oldEntry = Object.entries(item.specs || {}).find(
          ([k]) => k.toLowerCase() === attr.label.toLowerCase(),
        );
        if (oldEntry) migratedSpecs[attr.key] = oldEntry[1];
      }
    });
    if (targetAttrs.some(a => a.key === 'condition') && !migratedSpecs.condition && item.condition) {
      migratedSpecs.condition = item.condition === 'Brand New' ? 'New' : item.condition;
    }
    setForm({
      title:        item.title,
      description:  item.description || '',
      price:        String(item.price),
      category:     item.category || 'general',
      subcategory:  item.subcategory || '',
      location:     item.location || '',
      images:       item.images || [],
      specs:        migratedSpecs,
      condition:    item.condition || '',
      isNegotiable: item.isNegotiable || false,
      contactPhone: item.contactPhone || '',
      isFlashSale: item.isFlashSale || false,
      flashSalePrice: item.flashSalePrice != null ? String(item.flashSalePrice) : '',
      flashSaleEndsAt: item.flashSaleEndsAt ? new Date(item.flashSaleEndsAt).toISOString().slice(0, 16) : '',
      flashSaleQuantity: item.flashSaleQuantity != null ? String(item.flashSaleQuantity) : '',
    });
    setImagePreviews(item.images || []);
    setClassifiedLocation(locationFromText(item.location));
    setShowForm(true);
  };

  const cancelForm = () => {
    const hasDraft = Boolean(form.title.trim() || form.description.trim() || form.price || form.images.length);
    if (hasDraft && !window.confirm(t('listing_validation.discard_changes'))) return;
    resetForm();
    if (createOnly) onNavigate('back');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('seller_classifieds.confirm_delete'))) return;
    try { await api.delete(`/classifieds/${id}`); setMessage(t('seller_classifieds.listing_deleted')); fetchMyClassifieds(); }
    catch { setError(t('seller_classifieds.delete_failed')); }
  };

  const handleMarkSold = async (id) => {
    if (!window.confirm(t('seller_classifieds.confirm_mark_sold'))) return;
    try { await api.patch(`/classifieds/${id}/sold`); setMessage(t('seller_classifieds.marked_sold')); fetchMyClassifieds(); }
    catch { setError(t('seller_classifieds.update_failed')); }
  };

  const statusColor = (status) => ({
    active:  { backgroundColor: '#dcfce7', color: '#16a34a' },
    sold:    { backgroundColor: '#fee2e2', color: '#dc2626' },
    expired: { backgroundColor: '#fef9c3', color: '#ca8a04' },
  }[status] || { backgroundColor: '#f1f5f9', color: '#64748b' });

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: 90 }}>
      <BackBar onBack={() => onNavigate('back')} title={t('seller_classifieds.page_title')} />
      {/* createOnly is an immediate-create destination, not the manage
          dashboard — its own modal below is the entire view, so the list,
          stats and their banners (which would otherwise render duplicate
          error/message text alongside the modal's own) stay hidden. */}
      {!createOnly && (<>
      <div style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: '20px 16px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: '10px', flexWrap: 'wrap' }}>
              <button onClick={() => onNavigate('SellerDashboard')} style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>{t('seller_classifieds.back_dashboard')}</button>
              {/* Reachable regardless of seller-application status — any
                  listing owner can invoice a buyer on their own classified,
                  independent of the (still legitimately gated) full
                  business dashboard this button used to be the only door to. */}
              <button onClick={() => onNavigate('SellerInvoices')} style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>{t('seller_classifieds.invoices_button')}</button>
            </div>
            <h1 style={{ fontSize: '20px', fontWeight: '900', color: '#fff', margin: '0 0 4px', fontFamily: 'Manrope,sans-serif' }}>{t('seller_classifieds.page_title')}</h1>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', margin: 0 }}>{t('seller_classifieds.listings_count', { count: classifieds.length })}</p>
          </div>
          <button onClick={() => {
              if (!userPhone) {
                if (window.confirm(t('seller_classifieds.phone_required_confirm'))) {
                  onNavigate('CustomerProfile', { editField: 'phone' });
                }
                return;
              }
              resetForm(); setShowForm(true);
            }} style={{ background: '#fff', color: '#e91e63', border: 'none', padding: '10px 18px', borderRadius: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: '800' }}>{t('seller_classifieds.post_button')}</button>
        </div>
      </div>

      <div style={{ padding: '14px 16px 32px', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {message && <div style={{ backgroundColor: '#dcfce7', color: '#16a34a', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', fontWeight: '600', display: 'flex', justifyContent: 'space-between' }}><span>✅ {message}</span><button onClick={() => setMessage('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', fontWeight: 'bold' }}>×</button></div>}
        {error   && <div style={{ backgroundColor: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}><span>❌ {error}</span><button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}>×</button></div>}

        {/* Phone number warning */}
        {!userPhone && (
          <div style={{ backgroundColor: '#fef9c3', border: '2px solid #fcd34d', borderRadius: 12, padding: '14px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>📞</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e' }}>{t('seller_classifieds.phone_warning_title')}</div>
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>{t('seller_classifieds.phone_warning_desc')}</div>
            </div>
            <button onClick={() => onNavigate('CustomerProfile')}
              style={{ backgroundColor: '#f59e0b', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t('seller_classifieds.add_phone_button')}
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 18 }}>
          {[
            { label: t('seller_classifieds.stat_total'), value: classifieds.length, gradient: 'linear-gradient(135deg,#f093fb,#f5576c)', icon: '📋' },
            { label: t('seller_classifieds.stat_active'), value: classifieds.filter(c => c.status === 'active').length, gradient: 'linear-gradient(135deg,#43e97b,#38f9d7)', icon: '✅' },
            { label: t('seller_classifieds.stat_sold'), value: classifieds.filter(c => c.status === 'sold').length, gradient: 'linear-gradient(135deg,#667eea,#764ba2)', icon: '🏷️' },
          ].map(s => (
            <div key={s.label} style={{ background: s.gradient, borderRadius: 12, padding: '14px 8px', color: '#fff', textAlign: 'center' }}>
              <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 900 }}>{s.value}</div>
              <div style={{ fontSize: 11, opacity: 0.9 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}><div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div><p>{t('seller_classifieds.loading')}</p></div>
        ) : classifieds.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📋</div>
            <h3 style={{ color: '#1e293b', marginBottom: 8 }}>{t('seller_classifieds.no_listings_title')}</h3>
            <p style={{ color: '#64748b', marginBottom: 24 }}>{t('seller_classifieds.no_listings_desc')}</p>
            <button onClick={() => {
                if (!userPhone) {
                  if (window.confirm(t('seller_classifieds.phone_required_confirm'))) {
                    onNavigate('CustomerProfile');
                  }
                  return;
                }
                resetForm(); setShowForm(true);
              }} style={{ background: 'linear-gradient(135deg,#f093fb,#f5576c)', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>{t('seller_classifieds.post_first_listing')}</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
            {classifieds.map(item => (
              <div key={item.id} style={{ backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                <div style={{ height: 180, backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                  {item.images?.[0] ? <img src={item.images[0]} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 48 }}>📋</span>}
                  <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, ...statusColor(item.status) }}>{item.status?.toUpperCase()}</span>
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: 'linear-gradient(135deg,#f093fb,#f5576c)', color: '#fff' }}>
                      {CATEGORIES[item.category]?.icon} {item.category?.replace(/_/g,' ')}
                    </span>
                    {item.subcategory && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, backgroundColor: '#f0fdf4', color: '#16a34a' }}>
                        {CATEGORIES[item.category]?.subcategories?.[item.subcategory]?.label || item.subcategory}
                      </span>
                    )}
                    {item.condition && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, backgroundColor: '#eff6ff', color: '#1d4ed8' }}>{item.condition}</span>
                    )}
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{item.title}</h3>
                  {item.specs && Object.keys(item.specs).length > 0 && (
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      {Object.entries(item.specs).slice(0,2).map(([k,v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: 17, fontWeight: 900, color: '#e91e63' }}>TZS {Number(item.price).toLocaleString()}</span>
                    {item.isNegotiable && <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{t('seller_classifieds.negotiable_badge')}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => onNavigate(`ClassifiedDetail-${item.id}`)} style={{ flex: 1, backgroundColor: '#ede9fe', color: '#7c3aed', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{t('seller_classifieds.view_button')}</button>
                    {item.status === 'active' && <>
                      <button onClick={() => handleEdit(item)} style={{ flex: 1, backgroundColor: '#dbeafe', color: '#2563eb', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{t('seller_classifieds.edit_button')}</button>
                      <button onClick={() => handleMarkSold(item.id)} style={{ flex: 1, backgroundColor: '#dcfce7', color: '#16a34a', border: 'none', padding: 8, borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{t('seller_classifieds.sold_button')}</button>
                    </>}
                    <button onClick={() => handleDelete(item.id)} style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>🗑</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>)}

      {/* ── POST/EDIT MODAL ── */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.2)' }}>
          <div style={{ padding: '20px 16px 0', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div style={{ width: 40, height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 17, fontWeight: 900, color: '#1e293b', margin: '0 0 18px' }}>
              {editItem ? t('seller_classifieds.modal_edit_title') : t('seller_classifieds.modal_new_title')}
            </h2>

            {error && (
              <div role="alert" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                {error}
              </div>
            )}
            {/* Images */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>{t('seller_classifieds.photos_label')}{mediaRules.minImages > 0 ? ' *' : ''}</label>
              <p style={{ color: '#64748b', fontSize: 11, margin: '0 0 8px' }}>{mediaRules.guidanceText}</p>
              {imagePreviews.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  {imagePreviews.map((p, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={p} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '2px solid #e2e8f0' }} />
                      <button onClick={() => removeImage(i)} style={{ position: 'absolute', top: -6, right: -6, backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {imagePreviews.length < mediaRules.maxImages && (
                <div style={{ border: '2px dashed #e2e8f0', borderRadius: 8, padding: 12, textAlign: 'center', backgroundColor: '#f8fafc' }}>
                  <input type="file" accept="image/*" multiple disabled={uploading || saving} onChange={handleImageUpload} style={{ display: 'none' }} id="classifiedImages" />
                  <label htmlFor="classifiedImages" style={{ background: 'linear-gradient(135deg,#f093fb,#f5576c)', color: '#fff', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    {uploading ? t('seller_classifieds.uploading') : t('seller_classifieds.choose_photos')}
                  </label>
                  <p style={{ color: '#94a3b8', fontSize: 11, margin: '6px 0 0' }}>
                    {t('seller_classifieds.photos_count_hint', { count: imagePreviews.length })}/{mediaRules.maxImages}
                  </p>
                </div>
              )}
            </div>

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>{t('seller_classifieds.title_label')} *</label>
              <input type="text" placeholder={t('seller_classifieds.title_placeholder')} value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })} onBlur={handleTitleBlur} style={inputStyle} />
            </div>

            {/* Category + Subcategory */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>
                  {t('seller_classifieds.category_label')} *
                  {suggestingCategory && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{t('seller_classifieds.category_detecting')}</span>}
                  {categorySuggested && !suggestingCategory && <span style={{ marginLeft: 6, fontSize: 11, color: '#7c3aed', fontWeight: 700 }}>✨ {t('seller_classifieds.category_suggested')}</span>}
                </label>
                <select value={form.category} onChange={e => handleCategoryChange(e.target.value)} style={inputStyle}>
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{t('seller_classifieds.subcategory_label')}</label>
                <select value={form.subcategory} onChange={e => handleSubcategoryChange(e.target.value)} style={inputStyle}>
                  <option value="">{t('seller_classifieds.select_subcategory')}</option>
                  {subOptions.map(([key, sub]) => (
                    <option key={key} value={key}>{sub.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t('seller_classifieds.description_label')} *</label>
                <button type="button" onClick={generateDescription}
                  disabled={descGenerating || !form.title || !form.images.length}
                  title={!form.images.length ? t('seller_classifieds.ai_description_needs_photo') : ''}
                  style={{ fontSize: 11, fontWeight: 800, color: '#7c3aed', background: 'none',
                    border: '1px solid #c4b5fd', borderRadius: 8, padding: '4px 10px',
                    cursor: (descGenerating || !form.title || !form.images.length) ? 'not-allowed' : 'pointer',
                    opacity: (!form.title || !form.images.length) ? 0.5 : 1 }}>
                  {descGenerating ? `⏳ ${t('seller_classifieds.ai_description_generating')}` : `✨ ${t('seller_classifieds.ai_description_button')}`}
                </button>
              </div>
              <textarea placeholder={t('seller_classifieds.description_placeholder')} value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} />
            </div>

            {/* ── DYNAMIC, TYPE-AWARE CATEGORY ATTRIBUTES ── */}
            {/* Same schema-driven rendering as SellerProducts.js — see
                categories.data.ts's AttributeDef. Classifieds stay flexible
                (no client-side required-field block here — a listing can
                still be posted with a field left out; any server-side
                requirement, e.g. Make/Model for vehicles, surfaces through
                the existing error banner instead of blocking the form
                upfront). */}
            {attrFields.length > 0 && (
              <div style={{ backgroundColor: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
                  {t('seller_classifieds.listing_details_title')}
                  <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, marginLeft: 8 }}>{t('seller_classifieds.listing_details_hint')}</span>
                </div>
                {attrFields.some(a => !a.required && a.key !== 'condition') && (
                  <button type="button" onClick={() => setShowOptionalDetails(v => !v)}
                    aria-expanded={showOptionalDetails}
                    style={{ fontSize: 16, padding: '12px 0', border: 'none', background: 'none', color: '#1d4ed8', cursor: 'pointer' }}>
                    {t(showOptionalDetails ? 'listing_form.hide_details' : 'listing_form.optional_details')}
                  </button>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  {attrFields.filter(a => a.required || a.key === 'condition' || showOptionalDetails).sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map(attr => {
                    const currentValue = form.specs?.[attr.key] || '';
                    const fieldLabel = `${attr.label}${attr.unit ? ` (${attr.unit})` : ''}${attr.required ? ' *' : ''}`;
                    if (attr.type === 'select') {
                      return (
                        <div key={attr.key}>
                          <label style={{ ...labelStyle, marginBottom: 4 }}>{fieldLabel}</label>
                          <select value={currentValue} onChange={e => handleSpecChange(attr.key, e.target.value)}
                            style={{ ...inputStyle, padding: '10px 12px', fontSize: 16 }}>
                            <option value="">{t('seller_classifieds.select_subcategory')}</option>
                            {(attr.allowedValues || []).map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </div>
                      );
                    }
                    if (attr.type === 'multiselect') {
                      const selected = currentValue.split(',').map(v => v.trim()).filter(Boolean);
                      return (
                        <div key={attr.key} style={{ gridColumn: '1 / -1' }}>
                          <label style={{ ...labelStyle, marginBottom: 4 }}>{fieldLabel}</label>
                          {attr.allowedValues?.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {attr.allowedValues.map(v => (
                                <button type="button" key={v} onClick={() => toggleMultiSpec(attr.key, v)}
                                  style={{ padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                    border: selected.includes(v) ? '2px solid #2563eb' : '1px solid #e2e8f0',
                                    background: selected.includes(v) ? '#dbeafe' : '#fff', color: selected.includes(v) ? '#1d4ed8' : '#64748b' }}>
                                  {v}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <input type="text" value={currentValue} onChange={e => handleSpecChange(attr.key, e.target.value)}
                              style={{ ...inputStyle, padding: '10px 12px', fontSize: 16 }} />
                          )}
                        </div>
                      );
                    }
                    if (attr.type === 'boolean') {
                      return (
                        <div key={attr.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <label style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>{fieldLabel}</label>
                          <input type="checkbox" checked={currentValue === 'true'} onChange={e => handleSpecChange(attr.key, e.target.checked ? 'true' : 'false')} />
                        </div>
                      );
                    }
                    return (
                      <div key={attr.key}>
                        <label style={{ ...labelStyle, marginBottom: 4 }}>{fieldLabel}</label>
                        <input type={attr.type === 'number' ? 'number' : 'text'} placeholder={`e.g. ${attr.label}`}
                          value={currentValue}
                          onChange={e => handleSpecChange(attr.key, e.target.value)}
                          style={{ ...inputStyle, padding: '10px 12px', fontSize: 16 }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Condition + Negotiable */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: 8, border: '2px solid #e2e8f0', height: 40, boxSizing: 'border-box' }}>
                  <input type="checkbox" id="isNeg" checked={form.isNegotiable} onChange={e => setForm({ ...form, isNegotiable: e.target.checked })} style={{ width: 14, height: 14 }} />
                  <label htmlFor="isNeg" style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>{t('seller_classifieds.price_negotiable_label')}</label>
                </div>
              </div>
            {/* Flash Sale toggle */}
            <div style={{ backgroundColor:'#FFF5F5', border:'1.5px solid #FCA5A5', borderRadius:12, padding:14, marginTop:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <input type="checkbox" id="isFlash"
                  checked={form.isFlashSale}
                  onChange={e => setForm({ ...form, isFlashSale: e.target.checked })}
                  style={{ width:16, height:16, cursor:'pointer' }} />
                <label htmlFor="isFlash" style={{ fontSize:13, fontWeight:800,
                  color:'#DC2626', cursor:'pointer' }}>{t('seller_classifieds.flash_sale_label')}</label>
              </div>
              {form.isFlashSale && (
                <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:10 }}>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:'#64748B', display:'block', marginBottom:4 }}>
                      {t('seller_classifieds.flash_sale_price_label')}
                    </label>
                    <input type="number"
                      style={{ width:'100%', padding:'10px 12px', borderRadius:10,
                        border:'1.5px solid #FCA5A5', fontSize:15, fontWeight:900,
                        color:'#DC2626', outline:'none', boxSizing:'border-box' }}
                      placeholder={t('seller_classifieds.flash_sale_price_placeholder')}
                      value={form.flashSalePrice}
                      onChange={e => setForm({ ...form, flashSalePrice: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:'#64748B', display:'block', marginBottom:4 }}>
                      {t('seller_classifieds.flash_sale_ends_label')}
                    </label>
                    <input type="datetime-local"
                      style={{ width:'100%', padding:'10px 12px', borderRadius:10,
                        border:'1.5px solid #FCA5A5', fontSize:13, outline:'none', boxSizing:'border-box' }}
                      value={form.flashSaleEndsAt}
                      onChange={e => setForm({ ...form, flashSaleEndsAt: e.target.value })} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:700, color:'#64748B', display:'block', marginBottom:4 }}>
                      {t('seller_classifieds.flash_sale_qty_label')}
                    </label>
                    <input type="number"
                      style={{ width:'100%', padding:'10px 12px', borderRadius:10,
                        border:'1.5px solid #FCA5A5', fontSize:13, outline:'none', boxSizing:'border-box' }}
                      placeholder={t('seller_classifieds.flash_sale_qty_placeholder')}
                      value={form.flashSaleQuantity}
                      onChange={e => setForm({ ...form, flashSaleQuantity: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* Price + Location */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>{t('seller_classifieds.price_label')} *</label>
                <input type="number" min="1" step="1" placeholder={t('seller_classifieds.price_placeholder')} value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                  onFocus={() => fetchPriceSuggestion(form.category, form.title, form.specs?.condition)}
                  style={inputStyle} />
                {/* Price suggestion widget */}
                {(priceSuggestion || loadingPrice) && (
                  <div style={{ marginTop: 8, backgroundColor: '#eff6ff', borderRadius: 10, padding: '10px 14px', border: '1px solid #bfdbfe' }}>
                    {loadingPrice ? (
                      <div style={{ fontSize: 12, color: '#64748b' }}>{t('seller_classifieds.searching_market_prices')}</div>
                    ) : priceSuggestion && priceSuggestion.sampleSize > 0 ? (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', marginBottom: 6 }}>{t('seller_classifieds.price_suggestion_title')}</div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          {[
                            { label: t('seller_classifieds.price_low'),   value: priceSuggestion.low,  color: '#16a34a' },
                            { label: t('seller_classifieds.price_fair'), value: priceSuggestion.fair, color: '#1d4ed8' },
                            { label: t('seller_classifieds.price_high'),     value: priceSuggestion.high, color: '#7c3aed' },
                          ].map(r => (
                            <button key={r.label} onClick={() => setForm(f => ({ ...f, price: String(r.value) }))}
                              style={{ flex: 1, backgroundColor: '#fff', border: `1px solid ${r.color}`,
                                borderRadius: 8, padding: '6px 0', cursor: 'pointer', textAlign: 'center' }}>
                              <div style={{ fontSize: 10, color: r.color, fontWeight: 700 }}>{r.label}</div>
                              <div style={{ fontSize: 12, fontWeight: 900, color: r.color }}>TZS {Number(r.value).toLocaleString()}</div>
                            </button>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: '#475569' }}>{priceSuggestion.insights?.[0]}</div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
              <div>
                  <label style={labelStyle}>{t('seller_classifieds.location_label')} *</label>
                  <LocationPicker
                    value={classifiedLocation}
                    onLoadError={() => setError(t('listing_validation.location_load_failed'))}
                    onChange={loc => {
                      setClassifiedLocation(loc);
                      setForm(prev => ({
                        ...prev,
                        location: [loc.wardName, loc.districtName, loc.regionName].filter(Boolean).join(', '),
                      }));
                    }}
                  />
                  {form.location && (
                    <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>✅ {form.location}</div>
                  )}
              </div>
            </div>

            {/* Optional contact override — a side-hustle listing posted
                from a personal profile often needs a different number
                than the account's main contact; leave blank to use the
                account phone as before. */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('seller_classifieds.contact_phone_label')}</label>
              <input type="tel" placeholder={userPhone || t('seller_classifieds.contact_phone_placeholder')}
                value={form.contactPhone}
                onChange={e => setForm({ ...form, contactPhone: e.target.value })}
                style={inputStyle} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('seller_classifieds.contact_phone_hint')}</div>
            </div>
            <div style={{ height: 16 }} />
          </div>

          {/* Sticky footer — always visible, never scrolls out of view or
              hides behind the bottom nav, regardless of form length */}
          <div style={{ flexShrink: 0, display: 'flex', gap: 10,
            padding: '12px 16px max(12px, env(safe-area-inset-bottom))',
            borderTop: '1px solid #f1f5f9', backgroundColor: '#fff' }}>
              <button onClick={cancelForm} disabled={saving} style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', padding: 12, borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>{t('seller_classifieds.cancel_button')}</button>
              <button onClick={handleSubmit} disabled={uploading || saving}
                style={{ flex: 2, background: (uploading || saving) ? '#f48fb1' : 'linear-gradient(135deg,#f093fb,#f5576c)', color: '#fff', border: 'none', padding: 12, borderRadius: 8, cursor: (uploading || saving) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 800 }}>
                {saving ? t('listing_validation.saving') : uploading ? t('seller_classifieds.please_wait') : editItem ? t('seller_classifieds.update_listing_button') : t('seller_classifieds.post_listing_button')}
              </button>
          </div>
        </div>
        </div>
      )}

      {showVerifyIdentity && (
        <VerifyIdentityModal
          onClose={() => setShowVerifyIdentity(false)}
          onVerified={() => { setShowVerifyIdentity(false); handleSubmit(); }}
        />
      )}
    </div>
  );
};

export default SellerClassifieds;
